import { useEffect, useMemo, useRef, useState } from "react";
import { useSalon } from "../context/SalonContext";
import { buildSystemAlerts } from "../lib/alerts";
import { auth } from "../lib/api";
import { useWhatsAppStatus } from "../hooks/useWhatsAppStatus";
import { useSubscriptionsSummary } from "../hooks/useSubscriptionsSummary";
import { useWaitlistSummary } from "../hooks/useWaitlistSummary";

const NAV_GROUPS = [
    {
        id: "principal",
        label: "Principal",
        items: [
            { id: "home", label: "Home", icon: "fa-house", desc: "Painel inicial" },
            { id: "avisos", label: "Avisos", icon: "fa-bell", desc: "Diário + alertas do sistema" },
            { id: "agenda", label: "Agenda", icon: "fa-calendar-days", desc: "Hub central do salão" },
            { id: "pos", label: "Terminal Balcão", icon: "fa-cash-register", desc: "PDV touchscreen" },
            { id: "employeeapp", label: "App Colaborador", icon: "fa-mobile-screen", desc: "Agenda do dia + status" },
        ],
    },
    {
        id: "gestao",
        label: "Gestão",
        items: [
            { id: "employee", label: "Colaboradores", icon: "fa-users-gear", desc: "Cadastro + comissões" },
            { id: "clients", label: "Central de Clientes", icon: "fa-address-book", desc: "Ficha, gastos e agenda" },
            { id: "catalog", label: "Catálogo & Estoque", icon: "fa-boxes-stacked", desc: "Serviços e produtos" },
            { id: "admin", label: "Painel Financeiro", icon: "fa-chart-line", desc: "Métricas (PIN)" },
            { id: "whatsapp", label: "WhatsApp", icon: "fa-whatsapp", iconStyle: "brand", desc: "Conversas · Envios · Templates" },
            { id: "subscriptions", label: "Clube de Assinatura", icon: "fa-medal", desc: "Planos · Assinantes · Receita" },
        ],
    },
    {
        id: "sistema",
        label: "Sistema",
        items: [
            { id: "account", label: "Configurações", icon: "fa-sliders", desc: "Dados, Marca, PINs e plano" },
        ],
    },
];

const PIN_KEY = "kinng.sidebar.pinned";
const EXPANDED_KEY = "kinng.sidebar.expanded";

export const Sidebar = ({ forceOpen = false }) => {
    const { activeModule, setActiveModule, brand, theme, setLoggedIn, products, clients, appointments } = useSalon();
    const { status: waStatus } = useWhatsAppStatus(10000);
    const subsSummary = useSubscriptionsSummary(60000);
    const waitlistSummary = useWaitlistSummary(30000);
    const alertCount = useMemo(() => buildSystemAlerts({ products, clients, appointments, whatsapp: waStatus, subscriptionsSummary: subsSummary, waitlistSummary }).length, [products, clients, appointments, waStatus, subsSummary, waitlistSummary]);
    const criticalCount = useMemo(
        () => buildSystemAlerts({ products, clients, appointments, whatsapp: waStatus, subscriptionsSummary: subsSummary, waitlistSummary }).filter((a) => a.kind === "critical").length,
        [products, clients, appointments, waStatus, subsSummary, waitlistSummary]
    );
    const pendingBookingsCount = useMemo(
        () => (appointments || []).filter((a) => a.status === "awaiting_confirmation").length,
        [appointments]
    );

    // Persisted user preferences
    const [pinned, setPinned] = useState(() => {
        try {
            const raw = localStorage.getItem(PIN_KEY);
            return raw === null ? true : raw === "true";
        } catch {
            return true;
        }
    });
    const [userExpanded, setUserExpanded] = useState(() => {
        try {
            const raw = localStorage.getItem(EXPANDED_KEY);
            return raw === null ? true : raw === "true";
        } catch {
            return true;
        }
    });

    // Hover state (only relevant when not pinned)
    const [hovered, setHovered] = useState(false);
    const closeTimerRef = useRef(null);

    useEffect(() => {
        try { localStorage.setItem(PIN_KEY, String(pinned)); } catch { /* ignore */ }
    }, [pinned]);
    useEffect(() => {
        try { localStorage.setItem(EXPANDED_KEY, String(userExpanded)); } catch { /* ignore */ }
    }, [userExpanded]);

    // When unpinned, ignore quick mouseleave bounces (small grace period)
    const handleMouseEnter = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        if (!pinned) setHovered(true);
    };
    const handleMouseLeave = () => {
        if (!pinned) {
            closeTimerRef.current = setTimeout(() => setHovered(false), 180);
        }
    };

    useEffect(() => {
        if (pinned && closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
            setHovered(false);
        }
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, [pinned]);

    // Resolved expansion: forceOpen (Modo App) → sempre aberta e fixa;
    // caso contrário, pinned → respects user toggle; unpinned → flyout via hover.
    const expanded = forceOpen ? true : (pinned ? userExpanded : hovered);
    const effectivePinned = forceOpen ? true : pinned;
    const widthClass = expanded ? "md:w-72" : "md:w-[76px]";

    return (
        <aside
            data-testid="main-sidebar"
            data-expanded={expanded}
            data-pinned={effectivePinned}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`hidden md:flex flex-col ${widthClass} shrink-0 border-r border-white/5 bg-slate-950/80 backdrop-blur-xl relative z-20 transition-[width] duration-300 ease-in-out`}
        >
            {/* Header — logo + toggle controls */}
            <div className="px-3 lg:px-4 pt-5 pb-4 border-b border-white/5">
                <div className="flex items-start justify-between gap-2">
                    <div className={`flex items-start gap-3 min-w-0 transition-all duration-300 ${expanded ? "" : "justify-center w-full"}`}>
                        <div className="h-11 w-11 flex items-center justify-center shrink-0">
                            <img src="/brand/kinng-mark-transparent.png" alt="Kinng.app" className="h-full w-full object-contain" />
                        </div>
                        <div
                            className={`min-w-0 transition-all duration-300 ${
                                expanded ? "opacity-100 max-w-[160px]" : "opacity-0 max-w-0 pointer-events-none"
                            }`}
                        >
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Kinng.app
                                </span>
                                <PlanBadge plan={brand.plan} compact />
                                <SyncIndicator />
                            </div>
                            <div className="salon-heading text-base text-white truncate">
                                {brand.shopName}
                            </div>
                        </div>
                    </div>
                    {/* Toggle controls — visible only when expanded and NOT in forceOpen mode.
                        Stacked vertically: pin aligned with the top row (wordmark+plan badge),
                        collapse-arrow aligned with the title row below. */}
                    <div
                        className={`flex flex-col items-center justify-between shrink-0 transition-all duration-300 self-stretch py-0.5 ${
                            expanded && !forceOpen ? "opacity-100 max-w-[44px]" : "opacity-0 max-w-0 overflow-hidden pointer-events-none"
                        }`}
                    >
                        {/* Pin toggle (on top, in place of where the arrow used to be) */}
                        <button
                            data-testid="sidebar-pin-toggle"
                            onClick={() => setPinned((p) => !p)}
                            title={pinned ? "Desafixar barra (auto-ocultar ao sair)" : "Fixar barra (sempre aberta)"}
                            className={`shrink-0 h-8 w-8 rounded-lg border transition-all flex items-center justify-center ${
                                pinned
                                    ? "border-amber-400/60 bg-amber-500/15 text-amber-200 shadow-[0_0_15px_-5px_rgba(212,175,55,0.5)]"
                                    : "border-white/10 text-slate-400 hover:text-amber-200 hover:border-amber-500/40"
                            }`}
                        >
                            <i
                                className={`fa-solid fa-thumbtack text-xs transition-transform duration-300 ${
                                    pinned ? "rotate-0" : "rotate-45"
                                }`}
                            ></i>
                        </button>
                        {/* Hamburger / collapse */}
                        <button
                            data-testid="sidebar-collapse-toggle"
                            onClick={() => {
                                if (!pinned) {
                                    // Lock the bar in collapsed state when user explicitly collapses
                                    setPinned(true);
                                    setUserExpanded(false);
                                } else {
                                    setUserExpanded(false);
                                }
                            }}
                            title="Recolher menu"
                            className="shrink-0 h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-amber-200 hover:border-amber-500/40 transition-all flex items-center justify-center"
                        >
                            <i className="fa-solid fa-angles-left text-xs"></i>
                        </button>
                    </div>
                    {/* Expand button — visible only when collapsed (pinned) */}
                    {!expanded && (
                        <button
                            data-testid="sidebar-expand-toggle"
                            onClick={() => setUserExpanded(true)}
                            title="Expandir menu"
                            className="absolute -right-3 top-7 h-7 w-7 rounded-full border border-amber-500/40 bg-slate-900 text-amber-200 hover:bg-amber-500/15 transition-all flex items-center justify-center shadow-md z-30"
                        >
                            <i className="fa-solid fa-angles-right text-[10px]"></i>
                        </button>
                    )}
                </div>
                {/* Tenant info — only when expanded */}
                <div
                    className={`mt-4 flex items-center gap-2 text-xs text-slate-400 transition-all duration-300 ${
                        expanded ? "opacity-100" : "opacity-0 h-0 mt-0 overflow-hidden"
                    }`}
                >
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
                    <span className="whitespace-nowrap">
                        Tenant: <span className="font-mono text-slate-300">{brand.shopId}</span>
                    </span>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-2 lg:px-3 py-4 overflow-y-auto overflow-x-hidden">
                {NAV_GROUPS.map((group, gIdx) => (
                    <div key={group.id} className={gIdx > 0 ? "mt-4 pt-4 border-t border-white/5" : ""}>
                        <div
                            className={`px-3 pb-2 text-[10px] uppercase tracking-[0.28em] text-slate-600 transition-opacity duration-200 ${
                                expanded ? "opacity-100" : "opacity-0 h-0 pb-0 overflow-hidden"
                            }`}
                        >
                            {group.label}
                        </div>
                        <div className="space-y-1">
                            {group.items.map((item) => {
                                const active = activeModule === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        data-testid={`nav-${item.id}`}
                                        onClick={() => setActiveModule(item.id)}
                                        title={!expanded ? item.label : undefined}
                                        className={`group w-full min-h-[52px] flex items-center gap-3 px-2 lg:px-3 py-2.5 rounded-xl transition-all duration-300 relative ${
                                            active
                                                ? "bg-white/5 border border-white/10 shadow-lg"
                                                : "hover:bg-white/5 border border-transparent"
                                        } ${expanded ? "" : "justify-center"}`}
                                    >
                                        <div
                                            className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                                            style={
                                                item.id === "whatsapp"
                                                    ? { background: "rgba(16,185,129,0.15)", color: "#25D366" }
                                                    : active
                                                        ? { background: `${brand.primaryColor}22`, color: brand.primaryColor }
                                                        : { color: "#94a3b8", background: "rgba(15,23,42,0.6)" }
                                            }
                                        >
                                            <i className={`${item.iconStyle === "brand" ? "fa-brands" : "fa-solid"} ${item.icon}`}></i>
                                        </div>
                                        <div
                                            className={`text-left flex-1 min-w-0 transition-all duration-300 ${
                                                expanded
                                                    ? "opacity-100 max-w-[200px]"
                                                    : "opacity-0 max-w-0 pointer-events-none"
                                            }`}
                                        >
                                            <div
                                                className={`text-sm font-medium whitespace-nowrap ${
                                                    active ? "text-white" : "text-slate-300 group-hover:text-white"
                                                }`}
                                            >
                                                {item.label}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">{item.desc}</div>
                                        </div>
                                        {item.id === "avisos" && alertCount > 0 && (
                                            <span
                                                data-testid="nav-avisos-badge"
                                                title={`${alertCount} aviso${alertCount === 1 ? "" : "s"}${criticalCount > 0 ? ` (${criticalCount} crítico${criticalCount === 1 ? "" : "s"})` : ""}`}
                                                className={`absolute ${expanded ? "right-2 top-1/2 -translate-y-1/2" : "right-1 top-1"} min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 z-10 ${
                                                    criticalCount > 0
                                                        ? "bg-red-500 text-white animate-pulse shadow-[0_0_8px_-1px_rgba(239,68,68,0.7)]"
                                                        : "bg-amber-500 text-slate-950"
                                                }`}
                                            >
                                                {alertCount > 9 ? "9+" : alertCount}
                                            </span>
                                        )}
                                        {item.id === "agenda" && pendingBookingsCount > 0 && (
                                            <span
                                                data-testid="nav-agenda-pending-badge"
                                                title={`${pendingBookingsCount} solicitação${pendingBookingsCount === 1 ? "" : "ões"} aguardando confirmação`}
                                                className={`absolute ${expanded ? "right-2 top-1/2 -translate-y-1/2" : "right-1 top-1"} min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 z-10 bg-red-500 text-white animate-pulse shadow-[0_0_8px_-1px_rgba(239,68,68,0.7)]`}
                                            >
                                                {pendingBookingsCount > 9 ? "9+" : pendingBookingsCount}
                                            </span>
                                        )}
                                        {item.id === "whatsapp" && (waStatus?.unread || 0) > 0 && (
                                            <span
                                                data-testid="nav-whatsapp-unread-badge"
                                                title={`${waStatus.unread} mensagem${waStatus.unread === 1 ? "" : "s"} não lida${waStatus.unread === 1 ? "" : "s"}`}
                                                className={`absolute ${expanded ? "right-2 top-1/2 -translate-y-1/2" : "right-1 top-1"} min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 z-10 bg-emerald-500 text-slate-950 animate-pulse shadow-[0_0_8px_-1px_rgba(16,185,129,0.7)]`}
                                            >
                                                {waStatus.unread > 9 ? "9+" : waStatus.unread}
                                            </span>
                                        )}
                                        {active && expanded && (
                                            <span
                                                className="h-1.5 w-1.5 rounded-full shrink-0"
                                                style={{ background: brand.primaryColor }}
                                            ></span>
                                        )}
                                        {active && !expanded && (
                                            <span
                                                className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r"
                                                style={{ background: brand.primaryColor }}
                                            ></span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Footer */}
            <div className="p-2 lg:p-3 border-t border-white/5 space-y-2">
                <div
                    className={`rounded-xl border border-white/5 bg-slate-900/40 p-3 transition-all duration-300 ${
                        expanded ? "opacity-100" : "opacity-0 h-0 p-0 overflow-hidden border-0"
                    }`}
                >
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1 whitespace-nowrap">
                        Tema ativo
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <div className="salon-heading text-white text-sm truncate">{theme.name}</div>
                        <div
                            className="h-5 w-5 rounded-full border border-white/20 shrink-0"
                            style={{ background: brand.primaryColor }}
                        ></div>
                    </div>
                </div>
                <button
                    data-testid="logout-btn"
                    onClick={() => { auth.clear(); setLoggedIn(false); }}
                    title={!expanded ? "Sair" : undefined}
                    className={`w-full min-h-[44px] text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white border border-white/5 hover:border-white/20 rounded-xl py-2.5 transition-all flex items-center justify-center gap-2`}
                >
                    <i className="fa-solid fa-right-from-bracket"></i>
                    <span
                        className={`transition-all duration-300 whitespace-nowrap ${
                            expanded ? "opacity-100 max-w-[120px]" : "opacity-0 max-w-0 overflow-hidden"
                        }`}
                    >
                        Sair do Tenant
                    </span>
                </button>
            </div>
        </aside>
    );
};

// ─── Plan Badge ───────────────────────────────────────────────────────────
const PLAN_STYLES = {
    Classic: {
        icon: "fa-shield-halved",
        ring: "border-slate-500/40",
        bg: "bg-slate-700/30 text-slate-200",
    },
    Royal: {
        icon: "fa-chess-king",
        ring: "border-amber-400/60",
        bg: "bg-amber-500/15 text-amber-200",
        glow: "shadow-[0_0_12px_-4px_rgba(212,175,55,0.6)]",
    },
    Imperial: {
        icon: "fa-crown",
        ring: "border-amber-300",
        bg: "bg-gradient-to-r from-amber-500/30 to-amber-400/40 text-amber-100",
        glow: "shadow-[0_0_16px_-3px_rgba(251,191,36,0.75)]",
    },
};

const PLAN_ALIASES = { Premium: "Imperial", Pro: "Royal", Starter: "Classic" };

const SyncIndicator = () => {
    const { syncStatus } = useSalon();
    if (!syncStatus || syncStatus.state === "idle") return null;
    const online = syncStatus.state === "online";
    const color = online ? "bg-emerald-400" : "bg-red-400";
    const ring = online ? "ring-emerald-400/30" : "ring-red-400/30";
    const label = online ? "Sincronizado com a nuvem" : "Offline · usando cache local";
    return (
        <span
            data-testid="sidebar-sync-indicator"
            data-state={syncStatus.state}
            title={label}
            className={`inline-flex items-center gap-1 h-4 px-1 rounded-full ring-1 ${ring} bg-black/40`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${color} ${online ? "animate-pulse" : ""}`}></span>
        </span>
    );
};

const PlanBadge = ({ plan, compact = false }) => {    const canonical = PLAN_ALIASES[plan] || plan || "Classic";
    const style = PLAN_STYLES[canonical] || PLAN_STYLES.Classic;
    return (
        <span
            data-testid={`plan-badge-${canonical.toLowerCase()}`}
            className={`inline-flex items-center gap-1 rounded-md border ${style.ring} ${style.bg} ${style.glow || ""} ${compact ? "px-1.5 py-px text-[9px]" : "px-2 py-0.5 text-[10px]"} uppercase tracking-[0.15em] font-bold leading-none`}
        >
            <i className={`fa-solid ${style.icon} text-[9px]`}></i>
            {canonical}
        </span>
    );
};

export default Sidebar;
